import { FormProvider, useForm } from 'react-hook-form';
import { Modal, ModalBody, ModalHeader, Col, Row, ModalFooter, Label, Form } from 'reactstrap';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import FormInput from '@/components/forms/FormInput';
import { ReactComponent as CalendarIcon } from '@/assets/icons/calendar.svg';
import { ReactComponent as TetherGreen } from '@/assets/tokens/tether-green.svg';
import { TournamentStatus, GameType } from '@repo/common-types';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '@/redux/root';
import { setFilters, setIncludeArchived } from '@/redux/slices/tournamentFiltersSlice';

const MTTFiltersForm = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const { filters, includeArchived } = useSelector((state: AppState) => state.tournamentFilters);

  const formattedValuesFromFilters = {
    game: filters?.game?.[0] || undefined,
    status: filters?.status?.reduce((obj: any, status: string) => ({ ...obj, [status]: true }), {}) || {},
    startTime: {
      min: filters?.startTime?.from,
      max: filters?.startTime?.to,
    },
    buyIn: {
      min: filters?.buyIn?.min,
      max: filters?.buyIn?.max,
    },
    guaranteedPrize: {
      min: filters?.guaranteedPrizePool?.min,
      max: filters?.guaranteedPrizePool?.max,
    },
    includeArchived,
  };

  const form = useForm({
    defaultValues: formattedValuesFromFilters,
  });

  const { control, handleSubmit } = form;

  const onSubmit = (data: any) => {
    const newFilters: any = {};

    if (data.game) {
      newFilters.game = [data.game];
    }

    if (data.status) {
      const selectedStatuses = Object.entries(data.status)
        .filter(([_, v]) => v)
        .map(([k]) => k as TournamentStatus);
      if (selectedStatuses.length > 0) {
        newFilters.status = selectedStatuses;
      }
    }

    if (data.startTime?.min || data.startTime?.max) {
      newFilters.startTime = {
        from: data.startTime.min ? new Date(data.startTime.min) : undefined,
        to: data.startTime.max ? new Date(data.startTime.max) : undefined,
      };
    }

    if (data.buyIn?.min !== undefined || data.buyIn?.max !== undefined) {
      newFilters.buyIn = {
        min: data.buyIn.min ? Number(data.buyIn.min) : undefined,
        max: data.buyIn.max ? Number(data.buyIn.max) : undefined,
      };
    }

    if (data.guaranteedPrize?.min !== undefined || data.guaranteedPrize?.max !== undefined) {
      newFilters.guaranteedPrizePool = {
        min: data.guaranteedPrize.min ? Number(data.guaranteedPrize.min) : undefined,
        max: data.guaranteedPrize.max ? Number(data.guaranteedPrize.max) : undefined,
      };
    }

    dispatch(setFilters(newFilters));
    dispatch(setIncludeArchived(data.includeArchived || false));
    onClose();
  };

  return (
    <Modal centered isOpen>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <ModalHeader toggle={onClose} className="d-flex justify-content-between align-items-center w-full">
              Filters
            </ModalHeader>
          </div>
          <ModalBody>
            <Row>
              <Col>
                <FormInput
                  label="Game"
                  name="game"
                  control={control}
                  type="select"
                  options={Object.values(GameType).map((type) => ({
                    label: type,
                    value: type,
                  }))}
                />
              </Col>
              <Col>
                <Label>Status</Label>
                <div className="d-flex flex-row flex-wrap gap-1">
                  {Object.values(TournamentStatus).map((status) => (
                    <div key={status} style={{ width: '50%' }}>
                      <FormInput inline label={status} name={`status.${status}`} control={control} type="checkbox" />
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
            <Row>
              <Row>
                <Col>
                  <Label>Date Range</Label>
                </Col>
              </Row>
              <Col className="w-50">
                <FormInput
                  name="startTime.min"
                  control={control}
                  type="datetime-local"
                  placeholder="From"
                  Icon={CalendarIcon}
                />
              </Col>
              <Col className="w-50">
                <FormInput
                  name="startTime.max"
                  control={control}
                  type="datetime-local"
                  placeholder="To"
                  Icon={CalendarIcon}
                />
              </Col>
            </Row>
            <Row>
              <Row>
                <Col>
                  <Label>Buy-In Range</Label>
                </Col>
              </Row>
              <Col>
                <FormInput name="buyIn.min" control={control} type="number" placeholder="From" Icon={TetherGreen} />
              </Col>
              <Col>
                <FormInput name="buyIn.max" control={control} type="number" placeholder="To" Icon={TetherGreen} />
              </Col>
            </Row>
            <Row>
              <Row>
                <Col>
                  <Label>GTD Range</Label>
                </Col>
              </Row>
              <Col>
                <FormInput
                  name="guaranteedPrize.min"
                  control={control}
                  type="number"
                  placeholder="From"
                  Icon={TetherGreen}
                />
              </Col>
              <Col>
                <FormInput
                  name="guaranteedPrize.max"
                  control={control}
                  type="number"
                  placeholder="To"
                  Icon={TetherGreen}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <FormInput inline label="Include Archived" name="includeArchived" control={control} type="checkbox" />
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              className="btn btn-outline"
              variant={ButtonVariants.secondary}
              size={ButtonSizes.sm}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              color={ButtonVariants.primary}
              size={ButtonSizes.sm}
              onClick={handleSubmit(onSubmit)}
            >
              Apply Filter
            </Button>
          </ModalFooter>
        </Form>
      </FormProvider>
    </Modal>
  );
};

export default MTTFiltersForm;
